import { Box, Stack, Divider } from '@mui/material';
import { Button, Typography, colors, IconButton } from '@sweep-io/sweep-design';
import { Close, ChevronLeft, ChevronRight } from '@sweep-io/sweep-design/dist/icons';
import { BaseDialog } from '../../../common/dialogs/BaseDialog';
import { ArtifactCardIllustration } from '../ArtifactCardIllustration';
import { ArtifactTemplate } from './artifactTemplatesData';

interface ArtifactTemplatePreviewModalProps {
  open: boolean;
  template: ArtifactTemplate | null;
  onClose: () => void;
  onUseTemplate: (template: ArtifactTemplate) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

export const ArtifactTemplatePreviewModal = ({
  open,
  template,
  onClose,
  onUseTemplate,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: ArtifactTemplatePreviewModalProps) => {
  if (!template) return null;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxWidth: '720px',
          height: '580px',
          overflow: 'hidden',
        },
      }}
    >
      <Stack sx={{ height: '100%', position: 'relative' }}>
        {/* Navigation Arrows  */}
        <Box
          sx={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton variant="flat" size="small" onClick={onPrevious} disabled={!hasPrevious}>
            <ChevronLeft />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton variant="flat" size="small" onClick={onNext} disabled={!hasNext}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Close Button */}
        <Box sx={{ position: 'absolute', right: '16px', top: '16px', zIndex: 2 }}>
          <IconButton variant="flat" size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        {/* Content  */}
        <Box sx={{ px: 7, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* Header and title */}
          <Stack direction="row" gap={2} py={3} pb={3} alignItems="center" sx={{ flexShrink: 0 }}>
            <Box
              sx={{
                width: '104px',
                height: '104px',
                borderRadius: '8px',
                backgroundColor: colors.blue[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {template.image ? (
                <img
                  src={template.image}
                  alt={template.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <ArtifactCardIllustration />
              )}
            </Box>
            <Stack gap={0.5} sx={{ minWidth: 0, pr: 4 }}>
              <Typography variant="h3" color={colors.grey[900]}>
                {template.title}
              </Typography>
              <Typography variant="body" color={colors.grey[700]}>
                {template.subtitle}
              </Typography>
            </Stack>
          </Stack>

          <Divider sx={{ flexShrink: 0 }} />

          {/* Prompt Section - scrollable */}
          <Box sx={{ flex: 1, overflow: 'auto', pt: 3 }}>
            <Typography variant="body" color={colors.grey[700]}>
              Prompt
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body" color={colors.grey[900]}>
                {template.prompt}
              </Typography>
            </Box>
          </Box>

          {/* Actions */}
          <Stack direction="row" justifyContent="flex-end" py={3} pt={3} sx={{ flexShrink: 0 }}>
            <Button onClick={() => onUseTemplate(template)}>Use Template</Button>
          </Stack>
        </Box>
      </Stack>
    </BaseDialog>
  );
};
